import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  Calendar,
  Vote,
  FileSpreadsheet,
  CheckSquare,
  Users,
  Map,
  ArrowRight,
  TrendingUp,
  Clock,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsortiumDashboard = () => {
  const { consortiumId } = useParams();
  const navigate = useNavigate();
  const [consortium, setConsortium] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dynamic sidebar items with consortiumId
  const sidebarItems = [
    {
      section: "Consortium",
      items: [
        {
          name: "Overview",
          path: `/consortium/${consortiumId}`,
          icon: LayoutDashboard,
        },
        {
          name: "Agreements",
          path: `/consortium/${consortiumId}/agreements`,
          icon: FileText,
        },
        {
          name: "Meetings",
          path: `/consortium/${consortiumId}/meetings`,
          icon: Calendar,
        },
        {
          name: "Voting",
          path: `/consortium/${consortiumId}/voting`,
          icon: Vote,
        },
        {
          name: "RFPs & Bids",
          path: `/consortium/${consortiumId}/rfps`,
          icon: FileSpreadsheet,
        },
        {
          name: "Consent",
          path: `/consortium/${consortiumId}/consent`,
          icon: CheckSquare,
        },
      ],
    },
    {
      section: "Navigation",
      items: [
        { name: "My Dashboard", path: "/landowner", icon: LayoutDashboard },
      ],
    },
  ];

  useEffect(() => {
    fetchConsortiumData();
  }, [consortiumId]);

  const fetchConsortiumData = async () => {
    try {
      const [consortiumRes, membersRes] = await Promise.all([
        axios.get(`${API}/consortiums/${consortiumId}`),
        axios.get(`${API}/consortiums/${consortiumId}/members`),
      ]);
      setConsortium(consortiumRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error("Failed to fetch consortium data:", error);
      toast.error("Failed to load consortium");
      navigate("/landowner/consortiums");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout sidebarItems={sidebarItems}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-700 border-t-transparent" />
        </div>
      </DashboardLayout>
    );
  }

  if (!consortium) {
    return (
      <DashboardLayout sidebarItems={sidebarItems}>
        <div className="text-center py-16">
          <p className="text-slate-500">Consortium not found</p>
          <Button
            className="mt-4"
            onClick={() => navigate("/landowner/consortiums")}
          >
            Back to Consortiums
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const totalArea = members.reduce((sum, m) => sum + (m.total_area || 0), 0);

  const quickActions = [
    {
      title: "Agreement Formats",
      description: "View and create legal agreements",
      icon: FileText,
      path: `/consortium/${consortiumId}/agreements`,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Schedule Meeting",
      description: "Coordinate with members",
      icon: Calendar,
      path: `/consortium/${consortiumId}/meetings`,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Start Voting",
      description: "Create voting agenda",
      icon: Vote,
      path: `/consortium/${consortiumId}/voting`,
      color: "bg-blue-100 text-[#4E80FF]",
    },
    {
      title: "Manage RFPs",
      description: "Engage with developers",
      icon: FileSpreadsheet,
      path: `/consortium/${consortiumId}/rfps`,
      color: "bg-blue-100 text-[#4E80FF]",
    },
  ];

  return (
    <DashboardLayout sidebarItems={sidebarItems}>
      <div className="space-y-6" data-testid="consortium-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4E80FF] to-[#6B94FF] flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900">
                  {consortium.name}
                </h1>
                <p className="text-slate-500">{consortium.location}</p>
              </div>
            </div>
          </div>
          <Badge
            className={
              consortium.status === "active"
                ? "bg-[#4E80FF]"
                : consortium.status === "draft"
                ? "bg-yellow-500"
                : "bg-slate-500"
            }
          >
            {consortium.status}
          </Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Members</p>
                  <p className="font-heading text-2xl font-bold text-slate-900">
                    {members.length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#4E80FF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Area</p>
                  <p className="font-heading text-2xl font-bold text-slate-900">
                    {totalArea.toLocaleString()} sqm
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Map className="w-6 h-6 text-[#4E80FF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Land Parcels</p>
                  <p className="font-heading text-2xl font-bold text-slate-900">
                    {members.reduce((sum, m) => sum + (m.lands?.length || 0), 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#4E80FF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Target Progress</p>
                  <p className="font-heading text-2xl font-bold text-slate-900">
                    {consortium.target_area_sqm
                      ? `${Math.min(
                          100,
                          Math.round((totalArea / consortium.target_area_sqm) * 100)
                        )}%`
                      : "N/A"}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#4E80FF]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-4">
            Quick Actions
          </h2>
          <div className="grid md:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <Card
                key={index}
                className="card-hover cursor-pointer"
                onClick={() => navigate(action.path)}
                data-testid={`quick-action-${index}`}
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-lg ${action.color} flex items-center justify-center mb-4`}>
                    <action.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading font-semibold text-slate-900 mb-1">
                    {action.title}
                  </h3>
                  <p className="text-sm text-slate-500">{action.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Members List */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Members</CardTitle>
          </CardHeader>
          <CardContent>
            {members.length > 0 ? (
              <div className="space-y-4">
                {members.map((member, index) => (
                  <div
                    key={member.id || index}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-[#4E80FF] flex items-center justify-center text-white font-semibold">
                        {member.full_name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {member.full_name}
                        </p>
                        <p className="text-sm text-slate-500">{member.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        {member.total_area?.toLocaleString() || 0} sqm
                      </p>
                      <p className="text-sm text-slate-500">
                        {member.lands?.length || 0} parcels
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No members yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Target Progress */}
        {consortium.target_area_sqm && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-lg">
                Pooling Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Current Area</span>
                  <span className="font-medium">
                    {totalArea.toLocaleString()} / {consortium.target_area_sqm.toLocaleString()} sqm
                  </span>
                </div>
                <Progress
                  value={Math.min(100, (totalArea / consortium.target_area_sqm) * 100)}
                  className="h-3"
                />
                <p className="text-sm text-slate-500">
                  {(consortium.target_area_sqm - totalArea).toLocaleString()} sqm needed to reach target
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ConsortiumDashboard;
